import React from "react";
import styled from "styled-components";
import { withTranslation, WithTranslation } from "react-i18next";
import MediaQuery from "react-responsive";

import { H2Pre, H4, Body1Pre, Button } from "src/components/Typography";
import { colors } from "src/styles/colors";
import { media, mobileWidth, desktopWidth } from "src/utils/media";
import images from "src/images";
import { translate } from "src/locales";

type Props = {
  isKo: boolean;
  onLink: () => void;
} & WithTranslation;

const Container = styled.div`
  display: flex;
  flex-direction: row;
  background-color: white;
  ${media.mobile`
    flex-direction: column;
  `}
`;

const TabGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 109px;
  padding-top: 40px;
  padding-bottom: 36px;
  background-color: #fff;
  ${media.mobile`
    height: 72px;
    z-index: 1;
  `}
  ${media.desktop`
    display: none;
  `}
`;

const LeftGroup = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  background-color: #fff;
  ${media.mobile`
    flex: none;
    align-items: center;
    z-index: 1;
  `}
`;

const InnerLeftGroup = styled.div`
  display: flex;
  flex-direction: column;
  width: 835px;
  ${media.mobile`
    align-items: center;
    justify-content: center;
    width: 324px;
  `}
`;

const Title = styled(H2Pre)`
  color: #000;
  ${media.mobile`
    text-align: center;
  `}
`;

const ProfileGroup = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-top: 60px;
  ${media.mobile`
    flex-direction: column;
    margin-top: 24px;
  `}
`;

const ProfileImage = styled.img`
  min-width: 138px;
  min-height: 138px;
  width: 138px;
  height: 138px;
  margin-right: 24px;
  ${media.mobile`
    min-width: 72px;
    min-height: 72px;
    width: 72px;
    height: 72px;
    margin-bottom: 16px;
    margin-right: 0px;
  `}
`;

const ProfileContent = styled.div`
  display: flex;
  flex-direction: column;
  ${media.mobile`
    text-align: center;
  `}
`;

const ProfileName = styled(H4)`
  color: ${colors.text.dark.title};
`;

const ProfileDescription = styled(Body1Pre)`
  color: ${colors.text.dark.body};
  width: 800px;
  margin-top: 12px;
  ${media.mobile`
    width: auto;
    white-space: normal;
    margin-top: 4px;
  `}
`;

const ConfirmButton = styled(Button)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding-left: 56px;
  padding-right: 56px;
  height: 72px;
  color: #ffffff;
  background: ${colors.primary.default};
  box-shadow: 0px 10px 40px rgba(54, 62, 76, 0.05);
  border-radius: 100px;
  margin-top: 100px;
  margin-right: auto;
  transition: all 0.15s linear 0s;
  cursor: pointer;
  &:hover {
    background: ${colors.primary.hover};
  }
  ${media.mobile`
    padding-left: 38px;
    padding-right: 38px;
    height: 54px;
    margin-top: 32px;
    margin-left: auto;
    margin-right: auto;
  `}
`;

const PhoneImageView = styled.div`
  position: relative;
  width: 287px;
  height: 589px;
  ${media.mobile`
    width: calc(100% - 48px);
    height: 470px;
    margin: auto;
    overflow: hidden;
  `}
`;

const PhoneImage = styled.img`
  position: absolute;
  width: 287px;
  height: 589px;
  box-shadow: 0px 28px 40px rgb(7 7 8 / 0.2);
  border-radius: 42px;
  ${media.mobile`
    width: 100%;
    height: auto;
    box-shadow: none;
    border-radius: 0;
  `}
`;

const PhoneImageOverlay = styled.div`
  position: absolute;
  top: 0px;
  left: 0px;
  width: 100%;
  height: 470px;
  background: linear-gradient(
    0deg,
    #ffffff 0%,
    #ffffff 0%,
    rgba(255, 255, 255, 0) 35%
  );
`;

const UsecaseWalletServiceCard = (props: Props) => {
  const { isKo, t, onLink } = props;

  return (
    <Container>
      <LeftGroup>
        <InnerLeftGroup>
          <Title>
            <MediaQuery minWidth={desktopWidth}>
              {translate(["usecaseWalletServiceCard", "title"], t)}
            </MediaQuery>
            <MediaQuery maxWidth={mobileWidth}>
              {translate(["usecaseWalletServiceCard", "mobileTitle"], t)}
            </MediaQuery>
          </Title>
          <ProfileGroup>
            <ProfileImage
              src={images.usecaseWalletProfileSrc}
              srcSet={images.usecaseWalletProfile}
            />
            <ProfileContent>
              <ProfileName>
                {translate(["usecaseWalletServiceCard", "name"], t)}
              </ProfileName>
              <ProfileDescription>
                {translate(["usecaseWalletServiceCard", "description"], t)}
              </ProfileDescription>
            </ProfileContent>
          </ProfileGroup>
          <ConfirmButton onClick={onLink}>
            {translate(["usecaseWalletServiceCard", "confirmButton"], t)}
          </ConfirmButton>
        </InnerLeftGroup>
      </LeftGroup>
      <TabGroup></TabGroup>
      <PhoneImageView>
        <PhoneImage
          src={
            isKo
              ? images.usecaseWalletRightBgSrc
              : images.usecaseWalletRightEnBgSrc
          }
          srcSet={
            isKo ? images.usecaseWalletRightBg : images.usecaseWalletRightEnBg
          }
        />
        <MediaQuery maxWidth={mobileWidth}>
          <PhoneImageOverlay />
        </MediaQuery>
      </PhoneImageView>
    </Container>
  );
};

export default withTranslation()(UsecaseWalletServiceCard);
